import {TraderDBAccount, SelectedAccount} from '@qep/types/src';
import {Dropdown} from '../Dropdown/Dropdown';
import {Inputfield} from '../Inputfield/Inputfield';
import {isValidAccountText, notifyInvalidAccountText} from './accountTextValidation';
import './AccountSelector.scss';

interface Props {
  accounts: TraderDBAccount[];
  value: SelectedAccount | null | undefined;
  classes?: string;
  blockCustomization?: boolean;
  showSelectedLabel?: boolean;
  placeholder?: string;
  onChange?: (account: SelectedAccount) => void;
  invalidAccountTextFields: Record<'memo' | 'customText2', boolean>;
  setAccountTextFieldInvalid: (key: 'memo' | 'customText2', invalid: boolean) => void;
}

export const selectAccount = (account: TraderDBAccount, value: SelectedAccount): SelectedAccount => ({
  ...value,
  account,
  tradeIntention: account.tradeIntentions[0],
  customText2: account.customText2 || '',
  customText3: account.customText3 || '',
});

export default function AccountSelector(props: Props) {
  const {
    accounts,
    value,
    onChange = () => {},
    placeholder,
    invalidAccountTextFields: invalidFields,
    setAccountTextFieldInvalid: setFieldInvalidState,
  } = props;

  if (!value) {
    return null;
  }

  const accountDropDownItems = accounts.map(({id, code, description}) => ({
    id,
    code,
    name: description,
    description: `Acct: ${code}`,
  }));

  const getAccountById = (id: string | undefined) => accounts.find(account => account.id === id);

  const account: TraderDBAccount | undefined = getAccountById(value?.account?.id);
  const tradeIntentions = account?.tradeIntentions || [];

  const tradeIntentionDropDownItems = tradeIntentions?.map(tradeIntention => ({
    id: tradeIntention.id,
    name: tradeIntention.tradeIntentionName,
  })) || [];

  const onChangeAccount = (id: string) => {
    const account = getAccountById(id);
    if (!account) {
      return;
    }

    const newValue = selectAccount(account, value || null);
    onChange(newValue);
  };

  const changeValue = (name: string, updatedValue: any) => {
    if (typeof updatedValue === 'string' && (name === 'memo' || name === 'customText2')) {
      if (!isValidAccountText(updatedValue)) {
        setFieldInvalidState(name, true);
        notifyInvalidAccountText(updatedValue);
        return;
      }

      setFieldInvalidState(name, false);
    }

    onChange({...value, [name]: updatedValue});
  };

  return (
    <div style={{width: '100%', height: '100%'}}>
      <div className={`qep-account-selector ${props.classes || ''}`.trim()}>
        <div className='selector-section'>
          <Dropdown
            className='code-selector'
            id='account-code-selector'
            selectedItem={value?.account?.id || ''}
            items={accountDropDownItems}
            emptyMessage='No accounts available'
            onChange={onChangeAccount}
            searchable={true}
            placeholder={placeholder || 'Select Option'}
          />
          {props.showSelectedLabel && <span className='account'>
            <i>Acc.</i>&nbsp;{account?.code}
          </span>}
        </div>

        {!props.blockCustomization && <div>
          {/* we need to debounce here */}
          <Inputfield
            id='memo-field'
            value={value?.memo}
            inputClassName={invalidFields.memo ? 'error' : ''}
            className='memo'
            iconType='undo'
            placeholder='Memo'
            canClickIcon={!!(value?.memo)}
            onClickIcon={() => changeValue('memo', '')}
            onChange={value => changeValue('memo', value)}
            allowSpecialCharacters={false}
            maxLength={200}
          />
        </div>}
      </div >

      {!props.blockCustomization && <div className='flex qep-orderentry-account-selector'>
        <div>
          <Dropdown
            id='trade-intentions'
            className='code-selector'
            items={tradeIntentionDropDownItems}
            selectedItem={value?.tradeIntention?.id || ''}
            onChange={id => changeValue('tradeIntention', tradeIntentions.find(ti => ti.id === id))}
            customAllowSpecialCharacters={false}
            customMaxLength={200}
          />
        </div>

        <div>
          {/* we need to debounce here */}
          <Inputfield
            id='customText2'
            value={value?.customText2}
            inputClassName={invalidFields.customText2 ? 'error' : ''}
            className='note'
            iconType='undo'
            placeholder='Custom Text 2'
            canClickIcon={account?.customText2 !== value?.customText2}
            onClickIcon={() => changeValue('customText2', account?.customText2)}
            onChange={value => changeValue('customText2', value)}
            allowSpecialCharacters={false}
            maxLength={200}
          />
        </div>

        <div>
          {/* we need to debounce here */}
          <Inputfield
            id='customText3'
            value={value?.customText3}
            className='note'
            iconType='undo'
            placeholder='Custom Text 3'
            canClickIcon={account?.customText3 !== value?.customText3}
            onClickIcon={() => changeValue('customText3', account?.customText3)}
            onChange={value => changeValue('customText3', value)}
            allowSpecialCharacters={false}
            maxLength={200}
          />
        </div>
      </div>}
    </div >
  );
}
